import React, { useEffect, useState } from 'react';
import { ChevronLeft, FileText } from 'lucide-react';
import { formatPhone } from '../../../../utils/masks';
import { cn } from '../../../../utils/classnames';
import Button from '../../../../components/ui/buttons/Button';
import ProfileTab from './ProfileTab';
import SuitabilityTab from './tabs/SuitabilityTab';
import PersonalTab from './tabs/PersonalTab';
import AssetsAndProtectionsTab from './tabs/AssetsAndProtectionsTab';
import ObjectivesTab from './tabs/ObjectivesTab';
import IndicationsTab from './tabs/IndicationsTab';
import SpouseTab from './tabs/SpouseTab';
import { camelizeKeys } from '../../../../utils/camelizeKeys';
import DynamicActionButton from '../../../../components/ui/buttons/DynamicActionButton';

const ClientProfile = ({ client, sidebarItems }) => {
  const [profileCompletionProgress, setProfileCompletionProgress] = useState(0);
  const [sectionsCompleted, setSectionsCompleted] = useState(client.sectionsCompleted || {});
  const canWrite = client.accessLevel !== 'read';
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tab') || 'objectives';
  });

  const urlParams = new URLSearchParams(window.location.search);
  const from = urlParams.get('from') || 'commercial.prospects';

  const fetchProfileProgress = () => {
    fetch(`/app/planner/client/${client.id}/profile/completion_status`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setProfileCompletionProgress(camelData.profileCompletionProgress);
        setSectionsCompleted(camelData.sectionsCompleted);
      });
  };

  const handleTabSuccess = () => {
    fetchProfileProgress();

    if (activeTab === 'objectives') {
      setActiveTab('assets_and_protections');
    } else if (activeTab === 'assets_and_protections') {
      setActiveTab('suitability');
    } else if (activeTab === 'suitability') {
      setActiveTab('personal');
    } else if (activeTab === 'personal') {
      setActiveTab('spouse');
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    const url = new URL(window.location);
    url.searchParams.set('tab', key);
    window.history.pushState({}, '', url);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setProfileCompletionProgress(client.profileCompletionProgress || 0);
    }, 100);

    return () => clearTimeout(timeout);
  }, [client.profileCompletionProgress]);

  const tabs = [
    { key: 'objectives', label: 'OBJETIVOS E SONHOS', completed: sectionsCompleted.objectives },
    { key: 'assets_and_protections', label: 'ATIVOS E PROTEÇÕES', completed: sectionsCompleted.assetsAndProtections },
    { key: 'suitability', label: 'SUITABILITY', completed: sectionsCompleted.suitability },
    { key: 'personal', label: 'DADOS PESSOAIS', completed: sectionsCompleted.personal },
    { key: 'spouse', label: 'VINCULAR CÔNJUGE', completed: sectionsCompleted.spouse },
    { key: 'indications', label: 'INDICAÇÕES', completed: sectionsCompleted.indications },
  ];

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <h2 className="text-2xl font-semibold text-blueDarker flex items-center gap-2 font-jost">
          <FileText strokeWidth={1.25} className="w-6 h-6 text-tealMedium" />
          Consulta de Cliente
        </h2>

        <Button
          onClick={() => {
            if (from.startsWith('client_dashboard')) {
              const parts = from.split('_');
              if (parts.length >= 4) {
                const tab = parts[2];
                const scope = parts.slice(3).join('_');
                window.location.href = `/app/planner/client/${client.id}/dashboard?tab=${tab}&scope=${scope}`;
              } else {
                window.location.href = sidebarItems.find((item) => item.featureKey === from)?.href || '/app/planner/prospects';
              }
            } else if (from.startsWith('analyst_client_')) {
              const apiClientId = from.replace('analyst_client_', '');
              window.location.href = `/app/analyst/client/${apiClientId}/dashboard`;
            } else {
              window.location.href = sidebarItems.find((item) => item.featureKey === from)?.href || '/app/planner/prospects';
            }
          }}
          icon={ChevronLeft}
        >
          VOLTAR
        </Button>
      </div>

      <div className="bg-white rounded-tl-3xl rounded-tr-3xl p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-lg text-blueDarker">
          {client.status == 'active' ? (
            <a href={`/app/planner/client/${client.id}/dashboard?from=client-profile__${activeTab}`}>
              <p className="font-semibold flex items-center gap-2 hover:opacity-80 text-tealDeep">{client.name}</p>
            </a>
          ) : (
            <p className="font-semibold">{client.name}</p>
          )}

          <p>
            <span className="font-semibold">E-mail:</span> {client.email}
          </p>
          <p>
            <span className="font-semibold">Telefone:</span> {formatPhone(client.phone)}
          </p>
          <p>
            <span className="font-semibold">Perfil:</span> {client.riskProfile.text}
          </p>
        </div>
      </div>

      <div className="mb-8 bg-blueSoft relative rounded-bl-3xl rounded-br-3xl h-custom10">
        <div
          className={cn(
            'absolute left-0 top-0 h-full bg-tealLight transition-all duration-500 rounded-bl-3xl',
            profileCompletionProgress >= 95 && 'rounded-br-3xl'
          )}
          style={{ width: `${profileCompletionProgress}%` }}
        />
      </div>

      <div className="flex gap-3 border-t border-blueLight">
        {tabs.map((tab) => (
          <ProfileTab
            key={tab.key}
            label={tab.label}
            active={activeTab === tab.key}
            completed={tab.completed}
            onClick={() => handleTabChange(tab.key)}
          />
        ))}
      </div>

      <div className="mt-8">
        {activeTab === 'suitability' && (
          <SuitabilityTab clientId={client.id} onSuccess={handleTabSuccess} canWrite={canWrite} />
        )}
        {activeTab === 'assets_and_protections' && (
          <AssetsAndProtectionsTab clientId={client.id} onSuccess={handleTabSuccess} canWrite={canWrite} />
        )}
        {activeTab === 'objectives' && (
          <ObjectivesTab clientId={client.id} onSuccess={handleTabSuccess} canWrite={canWrite} />
        )}
        {activeTab === 'personal' && (
          <PersonalTab clientId={client.id} onSuccess={handleTabSuccess} canWrite={canWrite} />
        )}
        {activeTab === 'spouse' && <SpouseTab clientId={client.id} onSuccess={handleTabSuccess} canWrite={canWrite} />}
        {activeTab === 'indications' && (
          <IndicationsTab clientId={client.id} onSuccess={handleTabSuccess} canWrite={canWrite} />
        )}
      </div>
    </>
  );
};

export default ClientProfile;
